import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPanel({ token, onUnreadChange }) {
  const [customers, setCustomers] = useState(null);
  const [unreadMap, setUnreadMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const loadUnread = () => {
    axios
      .get(`${API}/messages/unread`, { headers })
      .then((r) => {
        const map = {};
        (r.data || []).forEach((row) => {
          map[row.customer_id] = row.unread;
        });
        setUnreadMap(map);
        onUnreadChange?.(Object.values(map).reduce((a, b) => a + b, 0));
      })
      .catch(() => {});
  };

  const loadThread = (cid) => {
    axios
      .get(`${API}/messages`, { headers, params: { customer_id: cid } })
      .then((r) => {
        setMessages(r.data || []);
        loadUnread();
      })
      .catch(() => {});
  };

  useEffect(() => {
    axios
      .get(`${API}/customers`, { headers })
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
    loadUnread();
    const iv = setInterval(loadUnread, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const iv = setInterval(() => loadThread(selected), 15000);
    return () => clearInterval(iv);
  }, [selected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selected || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { customer_id: selected, body: draft.trim() }, { headers });
      setDraft("");
      loadThread(selected);
    } catch (err) {
      /* keep draft on failure */
    } finally {
      setSending(false);
    }
  };

  if (customers === null)
    return (
      <div className="flex items-center justify-center gap-2 p-12 text-slate-400" data-testid="messages-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]" data-testid="messages-panel">
      <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-3">
        <p className="px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Customers</p>
        {customers.length === 0 && <p className="px-2 text-sm text-slate-500">No customers yet.</p>}
        {customers.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelected(c.id)}
            data-testid={`msg-customer-${c.id}`}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
              selected === c.id ? "bg-[#F25F22]/15 text-slate-50" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"
            }`}
          >
            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-600" />
            <span className="min-w-0 flex-1 truncate">{c.company_name}</span>
            {unreadMap[c.id] > 0 && (
              <span
                data-testid={`msg-unread-${c.id}`}
                className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 font-mono text-[10px] font-bold text-white"
              >
                {unreadMap[c.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex min-h-[420px] flex-col rounded-xl border border-white/[0.08] bg-[#12161F] p-4">
        {!selected ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-500" data-testid="msg-empty">
            Pick a customer to open your thread with them.
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-2.5 overflow-y-auto pb-3" data-testid="msg-thread">
              {messages.length === 0 && (
                <p className="pt-8 text-center text-sm text-slate-500">
                  No messages yet — say hello. They get an email heads-up and can reply from their portal.
                </p>
              )}
              {messages.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`msg-bubble-${i}`}
                    className={`max-w-[75%] rounded-xl px-3.5 py-2 text-[13px] leading-relaxed ${
                      m.sender_role === "admin"
                        ? "bg-[#F25F22] text-white"
                        : "border border-white/[0.08] bg-[#1A212E] text-slate-200"
                    }`}
                  >
                    {m.body}
                    <span className={`mt-1 block text-[9px] ${m.sender_role === "admin" ? "text-white/60" : "text-slate-500"}`}>
                      {m.sender_role === "admin" ? "You" : "Customer"} ·{" "}
                      {new Date(m.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-white/[0.06] pt-3">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="msg-input"
                placeholder="Message this customer…"
                className="w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                data-testid="msg-send"
                className="flex shrink-0 items-center gap-1.5 rounded-md bg-[#F25F22] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
